import { z } from 'zod'
import { CustomFieldIdSchema } from '../customField'
import { isNotEmpty } from '../../util/isEmpty'

export const LAUNCH_FIELDS = [
  'residentFirstName',
  'residentLastName',
  'residentFullName',
  'residentPhone',
  'residentEmail',
  'residentPrescreenDecision',
  'residentIndividualId',
  'addressFull',
  'addressLine1',
  'addressCity',
  'addressState',
  'addressZip',
  'latitude',
  'longitude',
  'accountId',
  'locationId',
  'deviceLatitude',
  'deviceLongitude',
  'userFullName',
  'userEmail',
  'userClientUserId',
  'userId',
  'userToken',
  'returnUrl',
] as const

export const LaunchFieldSchema = z.enum(LAUNCH_FIELDS)
export type LaunchField = z.infer<typeof LaunchFieldSchema>
export const isLaunchField = (value: unknown): value is LaunchField => LaunchFieldSchema.safeParse(value).success

export const UrlParamSourceTypeSchema = z.enum(['field', 'customField', 'static', 'object'])
export type UrlParamSourceType = z.infer<typeof UrlParamSourceTypeSchema>
export const isUrlParamSourceType = (value: unknown): value is UrlParamSourceType =>
  UrlParamSourceTypeSchema.safeParse(value).success

const ScalarParamSourceSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('field'), field: LaunchFieldSchema }),
  z.object({ type: z.literal('customField'), customFieldId: CustomFieldIdSchema }),
  z.object({ type: z.literal('static'), value: z.string() }),
])
export type ScalarParamSource = z.infer<typeof ScalarParamSourceSchema>

export const UrlParamWhenEmptySchema = z.enum(['exclude', 'emptyString', 'fallback', 'require'])
export type UrlParamWhenEmpty = z.infer<typeof UrlParamWhenEmptySchema>
export const isUrlParamWhenEmpty = (value: unknown): value is UrlParamWhenEmpty =>
  UrlParamWhenEmptySchema.safeParse(value).success

export type ObjectProperty = {
  name: string
  source: ParamSource
  whenEmpty: UrlParamWhenEmpty
  fallbackValue?: string
}

export type ObjectParamSource = {
  type: 'object'
  properties: ObjectProperty[]
}

export type ParamSource = ScalarParamSource | ObjectParamSource

const hasUniqueObjectPropertyNames = (properties: ObjectProperty[]): boolean =>
  new Set(properties.map((property) => property.name)).size === properties.length

export const ParamSourceSchema: z.ZodType<ParamSource> = z.lazy(() =>
  z.union([ScalarParamSourceSchema, ObjectParamSourceSchema])
)

export const ObjectPropertySchema: z.ZodType<ObjectProperty> = z
  .object({
    name: z.string().nonempty(),
    source: ParamSourceSchema,
    whenEmpty: UrlParamWhenEmptySchema,
    fallbackValue: z.string().optional(),
  })
  .refine((property) => property.whenEmpty !== 'fallback' || isNotEmpty(property.fallbackValue), {
    message: 'fallbackValue is required and must not be empty when whenEmpty is fallback',
    path: ['fallbackValue'],
  })
  .refine(
    (property) =>
      property.source.type !== 'field' || property.source.field !== 'userToken' || property.whenEmpty === 'require',
    {
      message: 'userToken properties must be required',
      path: ['whenEmpty'],
    }
  )

export const ObjectParamSourceSchema: z.ZodType<ObjectParamSource> = z
  .object({
    type: z.literal('object'),
    properties: ObjectPropertySchema.array(),
  })
  .refine((source) => hasUniqueObjectPropertyNames(source.properties), {
    message: 'object property names must be unique',
    path: ['properties'],
  })

export const LauncherOpenModeSchema = z.enum(['external', 'inAppBrowser', 'embedded'])
export type LauncherOpenMode = z.infer<typeof LauncherOpenModeSchema>

export const UrlParamSchema = z
  .object({
    name: z.string().nonempty(),
    source: ParamSourceSchema,
    whenEmpty: UrlParamWhenEmptySchema,
    fallbackValue: z.string().optional(),
  })
  .refine((param) => param.whenEmpty !== 'fallback' || isNotEmpty(param.fallbackValue), {
    message: 'fallbackValue is required and must not be empty when whenEmpty is fallback',
    path: ['fallbackValue'],
  })
  .refine(
    (param) => param.source.type !== 'field' || param.source.field !== 'userToken' || param.whenEmpty === 'require',
    {
      message: 'userToken parameters must be required',
      path: ['whenEmpty'],
    }
  )
export type UrlParam = z.infer<typeof UrlParamSchema>

const PathParamNameSchema = z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/, 'path param names must be simple identifiers')

export const PathParamSchema = z.object({
  name: PathParamNameSchema,
  source: ScalarParamSourceSchema,
  fallbackValue: z.string().nonempty(),
})
export type PathParam = z.infer<typeof PathParamSchema>

export const LauncherConfigSchema = z
  .object({
    baseUrl: z.url({ protocol: /^https$/ }),
    pathParams: PathParamSchema.array().optional(),
    params: UrlParamSchema.array(),
    openMode: LauncherOpenModeSchema,
    completionUrlPattern: z
      .string()
      .optional()
      .describe('Client-side completion URL prefix. Navigated URLs starting with this value complete the launcher.'),
  })
  .refine((launcher) => hasUniqueParamNames(launcher.params), {
    message: 'param names must be unique',
    path: ['params'],
  })
  .refine((launcher) => hasUniqueParamNames(launcher.pathParams ?? []), {
    message: 'path param names must be unique',
    path: ['pathParams'],
  })
  .refine((launcher) => hasValidPathPlaceholderNames(launcher.baseUrl), {
    message: 'path placeholders must use simple identifiers',
    path: ['baseUrl'],
  })
  .refine((launcher) => hasMatchingPathParams(launcher.baseUrl, launcher.pathParams ?? []), {
    message: 'pathParams must exactly match baseUrl placeholders',
    path: ['pathParams'],
  })
export type LauncherConfig = z.infer<typeof LauncherConfigSchema>

function hasUniqueParamNames(params: Array<UrlParam | PathParam>): boolean {
  return new Set(params.map((param) => param.name)).size === params.length
}

function hasValidPathPlaceholderNames(baseUrl: string): boolean {
  return getPathPlaceholderSegments(baseUrl).every((name) => PathParamNameSchema.safeParse(name).success)
}

function hasMatchingPathParams(baseUrl: string, pathParams: PathParam[]): boolean {
  const placeholders = new Set(getPathPlaceholderSegments(baseUrl))
  const paramNames = new Set(pathParams.map((param) => param.name))
  if (placeholders.size !== paramNames.size) return false
  return [...placeholders].every((name) => paramNames.has(name))
}

function getPathPlaceholderSegments(baseUrl: string): string[] {
  try {
    return new URL(baseUrl).pathname
      .split('/')
      .filter((segment) => segment.startsWith(':'))
      .map((segment) => segment.slice(1))
  } catch {
    return []
  }
}
